import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CadastroPacienteDto, PacienteCadastrado, PatientService } from '../../../services/patient/patient.service';
import { filtrarNomeSocial, MENSAGEM_NOME_SOCIAL_INVALIDO, nomeSocialValido } from '../../../interfaces/nome-social-validator';

/**
 * Cadastro de paciente que não existe no TASY. Grava pelo CartãoSiM (o mesmo caminho do
 * admin); a validação de verdade é do back, aqui só evita ida e volta à toa.
 */
@Component({
  selector: 'app-cadastro-paciente-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  // Sem modo escuro proprio: fica no tema claro mesmo com o app escuro.
  host: { class: 'tema-claro', style: 'display: contents' },
  templateUrl: './cadastro-paciente-modal.component.html',
})
export class CadastroPacienteModalComponent implements OnChanges {
  @Input() aberto = false;
  @Input() cpfInicial = '';
  @Input() clinicId = 0;

  @Output() fechar = new EventEmitter<void>();
  @Output() cadastrado = new EventEmitter<PacienteCadastrado>();

  form = CadastroPacienteModalComponent.formVazio();
  erros: string[] = [];
  isSalvando = false;
  isBuscandoCep = false;
  avisoNomeSocial = false;
  readonly MENSAGEM_NOME_SOCIAL_INVALIDO = MENSAGEM_NOME_SOCIAL_INVALIDO;

  constructor(private patientService: PatientService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberto']?.currentValue === true) {
      this.form = CadastroPacienteModalComponent.formVazio();
      this.form.cpf = (this.cpfInicial ?? '').replace(/\D/g, '').slice(0, 11);
      this.erros = [];
      this.isSalvando = false;
    }
  }

  private static formVazio() {
    return {
      cpf: '', nome: '', nomeSocial: '', nascimento: '', celular: '', sexo: '', estadoCivil: '',
      email: '', cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
    };
  }

  somenteDigitos(campo: 'cpf' | 'celular' | 'cep' | 'numero', limite: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.form[campo] = input.value.replace(/\D/g, '').slice(0, limite);
    input.value = this.form[campo];
  }

  /** Filtra ao digitar/colar: nunca deixa e-mail, telefone ou número virarem nome social. */
  onNomeSocialInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filtrado = filtrarNomeSocial(input.value);
    this.avisoNomeSocial = filtrado !== input.value;
    this.form.nomeSocial = filtrado;
    input.value = filtrado;
  }

  get nomeSocialInvalido(): boolean {
    return this.avisoNomeSocial || !nomeSocialValido(this.form.nomeSocial);
  }

  /** O form guarda só os dígitos; a máscara é só de exibição. */
  onCelularInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.form.celular = input.value.replace(/\D/g, '').slice(0, 11);
    input.value = this.formatarCelular(this.form.celular);
  }

  formatarCelular(d: string): string {
    if (!d) return '';
    if (d.length <= 2) return `(${d}`;
    const ddd = `(${d.slice(0, 2)}) `;
    const resto = d.slice(2);
    // 10 dígitos = fixo 9999-9999; 11 = celular 99999-9999
    const corte = d.length === 11 ? 5 : 4;
    return resto.length <= corte ? ddd + resto : `${ddd}${resto.slice(0, corte)}-${resto.slice(corte)}`;
  }

  async buscarCep(): Promise<void> {
    const cep = this.form.cep.replace(/\D/g, '');
    if (cep.length !== 8 || this.isBuscandoCep) return;
    this.isBuscandoCep = true;
    try {
      const data = await (await fetch('https://viacep.com.br/ws/' + cep + '/json/')).json();
      if (!data.erro) {
        this.form.rua    = data.logradouro || this.form.rua;
        this.form.bairro = data.bairro     || this.form.bairro;
        this.form.cidade = data.localidade || this.form.cidade;
        this.form.uf     = data.uf         || this.form.uf;
      }
    } catch { /* sem CEP o operador digita */ } finally {
      this.isBuscandoCep = false;
    }
  }

  private validar(): string[] {
    const erros: string[] = [];
    if (this.form.cpf.length !== 11) erros.push('CPF com 11 dígitos.');
    if (this.form.nome.trim().length < 5) erros.push('Nome completo.');
    if (!this.form.nascimento) erros.push('Data de nascimento.');
    if (this.form.celular.length < 10) erros.push('Celular com DDD.');
    if (!this.form.sexo) erros.push('Sexo.');
    if (!nomeSocialValido(this.form.nomeSocial)) erros.push(MENSAGEM_NOME_SOCIAL_INVALIDO);
    return erros;
  }

  async salvar(): Promise<void> {
    if (this.isSalvando) return;
    this.erros = this.validar();
    if (this.erros.length) return;

    this.isSalvando = true;
    try {
      const dados: CadastroPacienteDto = {
        ...this.form,
        clinicId: this.clinicId,
        nomeSocial: this.form.nomeSocial || null,
        estadoCivil: this.form.estadoCivil || null,
        email: this.form.email || null,
      };
      const paciente = await this.patientService.cadastrarPaciente(dados);
      this.cadastrado.emit(paciente);
    } catch (erro: any) {
      this.erros = [erro?.message ?? 'Não foi possível cadastrar o paciente.'];
    } finally {
      this.isSalvando = false;
    }
  }
}
